import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { FaIcon } from '../../FaIcon';
import { Spinner } from '../../Spinner';
import { PortalReportsApi } from '../../../api/portalReports';
import { EditViewMetadataModal } from './EditViewMetadataModal';
import { DuplicateViewModal } from '../../client/DuplicateViewModal';
import { YesNoModal } from '../../elements/YesNoModal';

/**
 * Selector de vistas del Explorador — replica la navegación de "sets de columnas" del tablero
 * (ConfigColumnsWidget + ColumnsSetsList): un control inline siempre visible que muestra la
 * vista activa, despliega la lista de vistas (asignadas al portal + mías) con la activa marcada
 * con check, y cambia de 1 click sin recargar (via onSelectView → Page.loadViewInline).
 *
 * Reemplaza el botón "folder-open" + "floppy-disk" del toolbar y al SavedViewBanner: las acciones
 * contextuales de la vista (dirty/guardar/descartar/renombrar/duplicar/salir) viven al lado del trigger.
 */
class SavedViewsSelectorComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            open: false,
            kebabOpen: false,
            views: [],
            loadingViews: false,
            errorViews: null,
            loadedClientId: null,
            showEditMetadata: false,
            showDuplicate: false,
            showDeleteConfirm: false,
        };
        this.wrapRef = React.createRef();
        this.handleClickOutside = this.handleClickOutside.bind(this);
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside(event) {
        if (this.wrapRef.current && !this.wrapRef.current.contains(event.target)) {
            if (this.state.open || this.state.kebabOpen) {
                this.setState({ open: false, kebabOpen: false });
            }
        }
    }

    toggleOpen = () => {
        const next = !this.state.open;
        this.setState({ open: next, kebabOpen: false });
        if (next && this.props.agencyClientId) {
            this.fetchViews();
        }
    };

    fetchViews() {
        const { t, agencyClientId } = this.props;
        // Stale-while-revalidate: si ya tenemos vistas del mismo cliente, las mostramos
        // mientras refrescamos (sin spinner). Sólo spinner en el primer fetch del cliente.
        const sameClient = this.state.loadedClientId === agencyClientId;
        this.setState({ loadingViews: !sameClient, errorViews: null });
        PortalReportsApi.listViews(agencyClientId)
            .then((response) => {
                if (!response || response.status === 'ERROR') {
                    this.setState({
                        loadingViews: false,
                        errorViews: (response && response.error_message) || t('saved_views.load_error'),
                    });
                    return;
                }
                this.setState({
                    views: Array.isArray(response.views) ? response.views : [],
                    loadingViews: false,
                    loadedClientId: agencyClientId,
                });
            })
            .catch(() => {
                this.setState({ loadingViews: false, errorViews: t('saved_views.load_error') });
            });
    }

    handleSelect = (view) => {
        this.setState({ open: false });
        if (this.props.onSelectView) this.props.onSelectView(view);
    };

    handleClear = () => {
        // "Exploración nueva": sale de la Vista (con guard de dirty en el Page). No-op si ya
        // no hay Vista cargada.
        this.setState({ open: false });
        if (this.props.currentView && this.props.onClearView) this.props.onClearView();
    };

    handleOpenPalette = () => {
        this.setState({ open: false });
        if (this.props.onOpenPalette) this.props.onOpenPalette();
    };

    _renderRow(view) {
        const { currentView } = this.props;
        const isActive = currentView && String(currentView.id) === String(view.id);
        return (
            <button
                key={view.id}
                type="button"
                className="de-view-switcher__option"
                onClick={() => this.handleSelect(view)}
            >
                <span className="de-view-switcher__option-name">{view.name}</span>
                {isActive && (
                    <FaIcon
                        iconName="check"
                        className="de-view-switcher__option-check"
                    />
                )}
            </button>
        );
    }

    _renderMenu() {
        const { t, currentView } = this.props;
        const { views, loadingViews, errorViews } = this.state;

        const assigned = views
            .filter((v) => v.assigned_to_portal)
            .sort((a, b) => (a.portal_order ?? 0) - (b.portal_order ?? 0));
        const personal = views.filter((v) => !v.assigned_to_portal);

        return (
            <div className="de-view-switcher__menu">
                {/* Estado vacío: ninguna Vista cargada */}
                <button
                    type="button"
                    className="de-view-switcher__option -free"
                    onClick={this.handleClear}
                >
                    <span className="de-view-switcher__option-name">
                        {t('saved_views.empty_state')}
                    </span>
                    {!currentView && (
                        <FaIcon iconName="check" className="de-view-switcher__option-check" />
                    )}
                </button>

                {loadingViews && (
                    <div className="de-view-switcher__menu-loading">
                        <Spinner color="positive small" />
                    </div>
                )}

                {!loadingViews && errorViews && (
                    <div className="de-view-switcher__menu-message">{errorViews}</div>
                )}

                {!loadingViews && !errorViews && views.length === 0 && (
                    <div className="de-view-switcher__menu-message">
                        {t('saved_views.palette_empty')}
                    </div>
                )}

                {!loadingViews && assigned.length > 0 && (
                    <div className="de-view-switcher__menu-section">
                        <div className="de-view-switcher__menu-title">
                            {t('saved_views.section_assigned')}
                        </div>
                        {assigned.map((v) => this._renderRow(v))}
                    </div>
                )}

                {!loadingViews && personal.length > 0 && (
                    <div className="de-view-switcher__menu-section">
                        <div className="de-view-switcher__menu-title -muted">
                            {t('saved_views.section_personal')}
                        </div>
                        {personal.map((v) => this._renderRow(v))}
                    </div>
                )}

                {/* Footer: buscar todas las vistas (de todos los clientes) */}
                {this.props.onOpenPalette && (
                    <button
                        type="button"
                        className="de-view-switcher__menu-footer"
                        onClick={this.handleOpenPalette}
                    >
                        <FaIcon iconName="magnifying-glass" style={{ marginRight: '0.4em' }} />
                        {t('saved_views.search_all_views')}
                    </button>
                )}
            </div>
        );
    }

    _renderDirtyActions() {
        const { t, dirty, onSaveChanges, onDiscardChanges, savingChanges } = this.props;
        if (!dirty) return null;
        return (
            <>
                <button
                    type="button"
                    className="btn -alt de-view-switcher__save"
                    onClick={onSaveChanges}
                    disabled={!!savingChanges}
                    title={t('saved_views.save_changes')}
                >
                    {savingChanges ? t('common.saving') : t('saved_views.save_changes')}
                </button>
                <button
                    type="button"
                    className="btn -negative de-view-switcher__discard"
                    onClick={onDiscardChanges}
                    disabled={!!savingChanges}
                    title={t('saved_views.reset_to_view')}
                >
                    {t('saved_views.reset_to_view')}
                </button>
            </>
        );
    }

    render() {
        const { t, currentView, dirty, agencyClientId, onSaveAsView, onViewUpdated, onClearView } = this.props;
        const { open, kebabOpen, showEditMetadata, showDuplicate, showDeleteConfirm } = this.state;

        const hasClient = !!agencyClientId;
        const isAssigned = currentView && !!currentView.assigned_to_portal;

        // El trigger es el indicador de la VISTA activa: "Vista: «X»" o "Exploración nueva".
        // (Las plantillas de columnas viven en el botón COLUMNAS, no acá.)
        const triggerName = currentView
            ? `${t('saved_views.trigger_view_prefix')} ${currentView.name}`
            : t('saved_views.empty_state');

        const modified = !!currentView && !!dirty;
        const triggerStateClass = modified ? '-modified' : '';

        return (
            <div className="de-view-switcher" ref={this.wrapRef}>
                <div className="de-view-switcher__select info-icon-container">
                    <button
                        type="button"
                        className={`de-view-switcher__trigger ${triggerStateClass}`}
                        onClick={this.toggleOpen}
                        disabled={!hasClient}
                        aria-expanded={open}
                        title={t('saved_views.what_is_view_hint')}
                    >
                        <FaIcon iconName="layer-group" className="de-view-switcher__trigger-icon" />
                        <span className="de-view-switcher__trigger-name">{triggerName}</span>
                        {modified && (
                            <span className="de-view-switcher__state-tag -modified">
                                <span className="de-view-switcher__dirty-dot" />
                                {t('saved_views.state_modified')}
                            </span>
                        )}
                        {isAssigned && (
                            <span className="saved-view-portal-badge">
                                {t('saved_views.badge_in_portal')}
                            </span>
                        )}
                        <FaIcon iconName="chevron-down" className="de-view-switcher__caret" />
                    </button>
                    {!hasClient && (
                        <div className="warning-tooltip -below">
                            <div className="warning-tooltip-body">
                                <p className="warning-tooltip-text">
                                    {t('saved_views.pick_client_to_see_views')}
                                </p>
                            </div>
                        </div>
                    )}
                    {open && hasClient && this._renderMenu()}
                </div>

                {/* Acciones contextuales al lado del selector */}
                <div className="de-view-switcher__actions">
                    {currentView && this._renderDirtyActions()}

                    {currentView && (
                        <div className="de-view-switcher__kebab-wrap">
                            <button
                                type="button"
                                className="de-view-switcher__icon-btn"
                                onClick={() => this.setState({ kebabOpen: !kebabOpen, open: false })}
                                title={t('saved_views.more_actions')}
                                aria-label={t('saved_views.more_actions')}
                            >
                                <FaIcon iconName="ellipsis-v" />
                            </button>
                            {kebabOpen && (
                                <div className="de-view-switcher__kebab-menu">
                                    {onSaveAsView && (
                                        <button onClick={() => { this.setState({ kebabOpen: false }); onSaveAsView(); }}>
                                            {t('saved_views.save_as_new_view')}
                                        </button>
                                    )}
                                    <button onClick={() => this.setState({ kebabOpen: false, showEditMetadata: true })}>
                                        {t('saved_views.action_rename_edit')}
                                    </button>
                                    <button onClick={() => this.setState({ kebabOpen: false, showDuplicate: true })}>
                                        {t('portal_reports.action_duplicate_to_others')}
                                    </button>
                                    <button
                                        className="-danger"
                                        onClick={() => this.setState({ kebabOpen: false, showDeleteConfirm: true })}
                                    >
                                        {t('saved_views.delete_view')}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {currentView && (
                        <button
                            type="button"
                            className="de-view-switcher__icon-btn"
                            onClick={onClearView}
                            title={t('saved_views.exit_view')}
                            aria-label={t('saved_views.exit_view')}
                        >
                            <FaIcon iconName="xmark" />
                        </button>
                    )}

                    {!currentView && onSaveAsView && (
                        <button
                            type="button"
                            className="btn -alt de-view-switcher__save-as"
                            onClick={onSaveAsView}
                            title={t('saved_views.save_as_title')}
                        >
                            <FaIcon iconName="floppy-disk" style={{ marginRight: '0.4em' }} />
                            {t('saved_views.save_as_title')}
                        </button>
                    )}
                </div>

                <EditViewMetadataModal
                    isOpen={showEditMetadata}
                    toggle={() => this.setState({ showEditMetadata: false })}
                    view={currentView}
                    onSaved={(updated) => {
                        if (onViewUpdated) onViewUpdated(updated);
                    }}
                />

                <DuplicateViewModal
                    isOpen={showDuplicate}
                    toggle={() => this.setState({ showDuplicate: false })}
                    sourceView={currentView}
                    currentClientId={currentView ? currentView.agency_client_id : agencyClientId}
                    onDuplicated={() => this.setState({ showDuplicate: false })}
                />

                {showDeleteConfirm && currentView && (
                    <YesNoModal
                        isOpen={showDeleteConfirm}
                        title={t('saved_views.delete_view_title')}
                        question={isAssigned
                            ? t('saved_views.delete_view_portal_warning', { name: currentView.name })
                            : t('saved_views.delete_view_confirm', { name: currentView.name })}
                        onYesAnswer={() => {
                            this.setState({ showDeleteConfirm: false });
                            if (this.props.onDeleteView) this.props.onDeleteView();
                        }}
                        onNoAnswer={() => this.setState({ showDeleteConfirm: false })}
                    />
                )}
            </div>
        );
    }
}

export const SavedViewsSelector = withTranslation()(SavedViewsSelectorComponent);
export default SavedViewsSelector;
